import { Button, Form, FormRule, Input, Select, SelectProps } from "antd";
import * as React from "react";
import { SelectRole } from "src/business/role/SelectRole/SelectRole";
import { ZSpecCommon } from "src/business/specification";
import { getWorkflowStepDisplayName } from "src/business/workflow/getWorkflowStepDisplayName";
import {
  getPredecessorOptions,
  isInitialStep,
  isUserStep,
} from "src/business/workflow/workflowSteps";
import {
  ZUserWorkflowStep,
  ZWorkflowStep,
  zWorkflowStep,
} from "src/business/workflow/ZWorkflowStep";
import { onError } from "src/common/onError";
import { maxNameLength, rxName } from "src/common/rxName";
import { trimRequired } from "src/common/validationRules";
import { IdBox } from "src/components/IdBox";
import { MdmDrawer } from "src/components/MdmDrawer";
import { ZSidLabel } from "src/types";

import { SelectWorkflowAttrs } from "./SelectWorkflowAttrs";
import { WorkflowStepExtensions } from "./WorkflowStepExtensions";
import * as styles from "./WorkflowStepModal.module.less";
import { stepTypeOptions } from "./workflowStepOptions";

type PropsWorkflowStepModal = {
  activeStep: ZWorkflowStep | undefined;
  close: () => void;
  allSteps: ZWorkflowStep[];
  specs: ZSpecCommon[];
  onApply: (step: ZWorkflowStep) => void;
};

const fld = (key: keyof ZUserWorkflowStep) => key;

export const WorkflowStepModal: React.FC<PropsWorkflowStepModal> = (props) => {
  const { activeStep, close, allSteps, specs, onApply } = props;
  const [form] = Form.useForm();
  React.useEffect(() => {
    if (activeStep) {
      form.resetFields();
      form.setFieldsValue(activeStep);
    }
  }, [activeStep]);
  const nameUnique: FormRule = {
    validator: (_, value: string | null | undefined) => {
      if (value && activeStep) {
        const double = allSteps.find(
          ({ id, name }) => id !== activeStep.id && name === value,
        );
        if (double)
          return Promise.reject(
            Error(`Название "${double.name}" уже использовано`),
          );
      }
      return Promise.resolve();
    },
  };
  const onFinish = (values: object) => {
    try {
      const step = zWorkflowStep.parse({ ...activeStep, ...values });
      onApply(step);
      close();
    } catch (e) {
      onError(e);
    }
  };
  const predecessorOptions = activeStep
    ? getPredecessorOptions(activeStep, allSteps)
    : [];
  // У Initial нет предшественников
  const isInitial = !!activeStep && isInitialStep(activeStep);
  return (
    <MdmDrawer
      open={!!activeStep}
      onClose={close}
      title="Настройки шага"
      extra={
        <Button type="primary" onClick={() => form.submit()}>
          Применить
        </Button>
      }
    >
      {!!activeStep && (
        <Form
          form={form}
          layout="vertical"
          className={styles.form}
          onFinish={onFinish}
        >
          <div className={styles.content}>
            <Form.Item
              label="Тип шага"
              name={fld("type")}
              rules={[{ required: true, message: "Не указан тип шага" }]}
            >
              <Select disabled options={stepTypeOptions} />
            </Form.Item>
            <Form.Item
              label="Системное название"
              name={fld("name")}
              rules={[
                trimRequired(),
                { pattern: rxName, message: "Недопустимые символы" },
                { max: maxNameLength },
                nameUnique,
              ]}
            >
              <Input
                allowClear
                maxLength={maxNameLength}
                disabled={!!activeStep.name}
              />
            </Form.Item>
            <IdBox entity={activeStep} />
            <Form.Item
              label="Название"
              name={fld("displayName")}
              rules={[trimRequired(), { max: maxNameLength }]}
            >
              <Input allowClear maxLength={maxNameLength} />
            </Form.Item>
            <Form.Item label="Описание" name={fld("description")}>
              <Input.TextArea allowClear />
            </Form.Item>
            {/* У системного шага нет исполнителей и атрибутов */}
            {isUserStep(activeStep) && (
              <Form.Item
                label="Исполнители"
                name={fld("roles")}
                rules={[
                  {
                    required: true,
                    message: "Необходимо добавить хотя бы одну роль",
                  },
                ]}
              >
                <SelectRole multiple />
              </Form.Item>
            )}
            {!isInitial && (
              <Form.Item name={fld("predecessors")} label="Предшественник">
                <SelectSidLabel
                  placeholder="Выбрать"
                  options={predecessorOptions.map(({ step, disabled }) => ({
                    id: step.id,
                    label: getWorkflowStepDisplayName(step),
                    disabled,
                    // Initial отделён чертой от пользовательских шагов
                    className:
                      isInitialStep(step) && predecessorOptions.length > 1
                        ? styles.initialOption
                        : undefined,
                  }))}
                  mode="multiple"
                />
              </Form.Item>
            )}
            {/* По постановке «Системные шаги в РП», R1.1 п. 8, блок есть у всех шагов */}
            <Form.Item name={fld("extensions")} noStyle>
              <WorkflowStepExtensions />
            </Form.Item>
            {isUserStep(activeStep) && <SelectWorkflowAttrs specs={specs} />}
          </div>
        </Form>
      )}
    </MdmDrawer>
  );
};

/**
 * Компонент, аналог Select, но для value используется тип ZSidLabel
 */

type SidLabelOption = ZSidLabel & { disabled?: boolean; className?: string };

type PropsSelectSidLabel = Omit<
  SelectProps<string[] | string, SidLabelOption>,
  "fieldNames" | "onChange" | "options" | "value"
> & {
  options: SidLabelOption[];
  value?: ZSidLabel | ZSidLabel[];
  onChange?(newValue?: ZSidLabel | ZSidLabel[]): void;
};

const SelectSidLabel: React.FC<PropsSelectSidLabel> = (props) => {
  const { value, onChange, ...restProps } = props;
  const handleChange = (rawValue: unknown) => {
    let newValue: ZSidLabel | ZSidLabel[] | undefined;
    const dict: Record<string, string> = {};
    restProps.options.forEach(({ id, label }) => {
      dict[id] = label;
    });
    const mkOpt = (id: string): ZSidLabel => ({ id, label: dict[id] ?? id });
    if (Array.isArray(rawValue)) {
      newValue = rawValue.map(mkOpt);
    } else if (typeof rawValue === "string") {
      newValue = mkOpt(rawValue);
    }
    onChange?.(newValue);
  };
  return (
    <Select<string[] | string, ZSidLabel>
      {...restProps}
      fieldNames={{ value: "id" }}
      value={Array.isArray(value) ? value.map(({ id }) => id) : value?.id}
      onChange={handleChange}
    />
  );
};
